import React, { useCallback, useContext, useEffect, useId, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogActions } from '@mui/material';
import { UNSAFE_DataRouterContext, useBlocker } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FiCheck, FiTrash2, FiX } from 'react-icons/fi';
import api from '../../api/api';
import { useUserStore } from '../../stores/userStore';
import { archiveGoalDefinitions } from './generated/archiveGoals';
import { archiveKey, archivePayload, goalCopy, goalDrafts, goalPayload } from './archiveModel';
import { useArchiveQuery } from './useMyArchive';
function RouteGuard({ state, onDiscard }) {
  const blocker = useBlocker(
    () => !state.current.released && (state.current.dirty || state.current.busy),
  );
  useEffect(() => {
    if (blocker.state === 'blocked') {
      if (state.current.busy) blocker.reset();
      else
        onDiscard({
          cancel: () => blocker.reset(),
          run: () => {
            state.current.released = true;
            blocker.proceed();
          },
        });
    }
  }, [blocker, state, onDiscard]);
  return null;
}
export default function ArchiveGoalEditor({
  id,
  nickname,
  initialPeriod,
  expanded = false,
  t,
  style,
  onClose,
}) {
  const [period, setPeriod] = useState(initialPeriod),
    [drafts, setDrafts] = useState(() => goalDrafts(null, initialPeriod)),
    [baseline, setBaseline] = useState(() => goalDrafts(null, initialPeriod));
  const [phase, setPhase] = useState('idle'),
    [error, setError] = useState(null),
    [confirm, setConfirm] = useState(null);
  const query = useArchiveQuery(id, period, true),
    client = useQueryClient(),
    titleId = useId();
  const mounted = useRef(true),
    timer = useRef(),
    request = useRef(),
    lock = useRef(false),
    guard = useRef({}),
    router = useContext(UNSAFE_DataRouterContext);
  const dirty = JSON.stringify(drafts) !== JSON.stringify(baseline),
    busy = phase !== 'idle';
  guard.current = { ...guard.current, dirty, busy };
  const latest = useRef();
  latest.current = { drafts, baseline, period, onClose };
  const session = useRef({
    token: localStorage.getItem('POSTPOP_AUTH_TOKEN'),
    login: useUserStore.getState().isLogin,
    valid: true,
  });
  const sameAccount = useCallback(() => {
    const user = useUserStore.getState().user;
    return (
      session.current.valid &&
      session.current.token === localStorage.getItem('POSTPOP_AUTH_TOKEN') &&
      session.current.login === useUserStore.getState().isLogin &&
      String(user?.id) === String(id) &&
      user?.nickname === nickname
    );
  }, [id, nickname]);
  useEffect(
    () =>
      useUserStore.subscribe?.(() => {
        if (!session.current.valid || sameAccount()) return;
        session.current.valid = false;
        request.current?.abort();
        clearTimeout(timer.current);
        guard.current.released = true;
        latest.current.onClose();
      }),
    [sameAccount],
  );
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(timer.current);
      request.current?.abort();
    };
  }, []);
  useEffect(() => {
    if (
      query.data &&
      JSON.stringify(latest.current.drafts) === JSON.stringify(latest.current.baseline) &&
      !lock.current
    ) {
      const next = goalDrafts(query.data, period);
      setDrafts(next);
      setBaseline(next);
    }
  }, [query.data, period]);
  useEffect(() => {
    const protect = (e) => {
      if (!guard.current.released && (guard.current.dirty || guard.current.busy)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', protect);
    return () => window.removeEventListener('beforeunload', protect);
  }, []);
  const leave = () => {
    guard.current.released = true;
    latest.current.onClose();
  };
  const close = () => {
    if (lock.current || confirm) return;
    if (dirty) setConfirm({ run: leave });
    else leave();
  };
  const changePeriod = (next) => {
    if (next === period || lock.current) return;
    const apply = () => {
      const nextDrafts = goalDrafts(client.getQueryData(archiveKey(id, next)), next);
      setBaseline(nextDrafts);
      setDrafts(nextDrafts);
      setError(null);
      setPeriod(next);
    };
    if (dirty) setConfirm({ run: apply });
    else apply();
  };
  const submit = async (deleting = false) => {
    if (lock.current || query.isFetching || !query.data || !sameAccount()) return;
    const value = goalPayload(drafts);
    if (!deleting && value.error) {
      setError(t(value.error));
      return;
    }
    lock.current = true;
    setPhase(deleting ? 'deleting' : 'saving');
    setError(null);
    const submitted = { ...drafts },
      submittedPeriod = period;
    const controller = new AbortController();
    request.current = controller;
    try {
      const response = deleting
        ? await api.deleteAuth(`/me/archive/goal?periodType=${submittedPeriod}`, {
            signal: controller.signal,
          })
        : await api.putAuth(`/me/archive/goal?periodType=${submittedPeriod}`, value.payload, {
            signal: controller.signal,
          });
      const data = archivePayload(response, submittedPeriod);
      if (!mounted.current || !sameAccount()) return;
      client.setQueryData(archiveKey(id, submittedPeriod), data);
      // Acknowledged mutation remains visible even if the subsequent GET fails.
      void client.invalidateQueries({ queryKey: ['me', 'archive', id] });
      setBaseline(submitted);
      guard.current.dirty = false;
      if (deleting) {
        leave();
        return;
      }
      setPhase('success');
      timer.current = setTimeout(() => {
        if (mounted.current && sameAccount()) leave();
      }, 650);
    } catch (e) {
      if (mounted.current && sameAccount() && !controller.signal.aborted) {
        setError(
          t(deleting ? 'archive.editor.deleteFailedTitle' : 'archive.editor.saveFailedTitle'),
        );
        setPhase('idle');
        lock.current = false;
      }
    }
  };
  const locked = busy || query.isFetching || !query.data;
  const cancelConfirm = () => {
    confirm?.cancel?.();
    setConfirm(null);
  };
  return (
    <>
      {router && <RouteGuard state={guard} onDiscard={setConfirm} />}
      <Dialog
        open
        className={`pp-archive-dialog ${expanded ? 'is-expanded' : ''}`}
        fullWidth
        maxWidth="sm"
        onClose={close}
        aria-labelledby={titleId}
        PaperProps={{ style }}
      >
        <header className="pp-archive-editor-header">
          <button
            className="pp-archive-icon"
            aria-label={t('archive.editor.cancelA11y')}
            disabled={busy}
            onClick={close}
          >
            <FiX size={22} />
          </button>
          <DialogTitle id={titleId}>
            {t(period === 'WEEK' ? 'archive.editor.weekTitle' : 'archive.editor.monthTitle')}
          </DialogTitle>
          <div>
            {query.data?.goal.isConfigured && (
              <button
                className="pp-archive-icon is-delete"
                aria-label={t(
                  phase === 'deleting'
                    ? 'archive.editor.deletingA11y'
                    : 'archive.editor.deleteA11y',
                )}
                disabled={locked}
                onClick={() => setConfirm({ delete: true, run: () => submit(true) })}
              >
                <FiTrash2 size={21} />
              </button>
            )}
            <button
              className={`pp-archive-icon is-save ${phase === 'success' ? 'is-success' : ''}`}
              aria-label={t(
                phase === 'success'
                  ? 'archive.editor.savedA11y'
                  : phase === 'saving'
                    ? 'archive.editor.savingA11y'
                    : 'archive.editor.saveA11y',
              )}
              disabled={locked}
              onClick={() => submit()}
            >
              {phase === 'saving' ? <span className="pp-archive-spinner" /> : <FiCheck size={23} />}
            </button>
          </div>
        </header>
        <DialogContent className="pp-archive-editor-content">
          <div
            className="pp-archive-editor-period"
            role="tablist"
            aria-label={t('my.musicArchive')}
          >
            {['WEEK', 'MONTH'].map((option) => (
              <button
                key={option}
                role="tab"
                aria-selected={option === period}
                disabled={busy}
                onClick={() => changePeriod(option)}
              >
                {t(option === 'WEEK' ? 'archive.editor.week' : 'archive.editor.month')}
              </button>
            ))}
          </div>
          <p>{t('archive.editor.description')}</p>
          {query.isPending && <p role="status">{t('archive.homeLoading')}</p>}
          {query.isError && (
            <div className="pp-archive-error" role="alert">
              {t('archive.loadError')}
              <button onClick={() => query.refetch()} disabled={busy}>
                {t('common.retry')}
              </button>
            </div>
          )}
          {error && (
            <p role="alert" className="pp-archive-error">
              {error}
            </p>
          )}
          {phase === 'success' && <p role="status">{t('archive.editor.savedA11y')}</p>}
          {archiveGoalDefinitions.map((def) => {
            const copy = goalCopy(def.type),
              label = t(`archive.editor.${copy.label}`),
              suffix = t(`archive.editor.${copy.suffix}`);
            return (
              <div className="pp-archive-goal-editor" key={def.type}>
                <label htmlFor={`${titleId}-${def.type}`}>{label}</label>
                <div className="pp-archive-input-row">
                  <input
                    id={`${titleId}-${def.type}`}
                    aria-label={t('archive.editor.inputA11y', { label })}
                    inputMode="numeric"
                    maxLength={3}
                    value={drafts[def.type]}
                    disabled={busy || query.isPending || !query.data}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      setError(null);
                      setDrafts({
                        ...drafts,
                        [def.type]: e.target.value.replace(/[^0-9]/g, '').slice(0, 3),
                      });
                    }}
                  />
                  <span>{suffix}</span>
                </div>
                <div className="pp-archive-presets">
                  {(period === 'WEEK' ? def.weekPresets : def.monthPresets).map((n) => (
                    <button
                      key={n}
                      disabled={busy || !query.data}
                      aria-pressed={Number(drafts[def.type]) === n}
                      onClick={() => {
                        setError(null);
                        setDrafts({ ...drafts, [def.type]: String(n) });
                      }}
                    >
                      {n}
                      {suffix}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </DialogContent>
      </Dialog>
      {confirm && (
        <Dialog
          open
          fullWidth
          maxWidth="xs"
          className="pp-archive-dialog pp-archive-confirm"
          onClose={cancelConfirm}
          PaperProps={{ style }}
          aria-labelledby={`${titleId}-confirm`}
        >
          <DialogTitle id={`${titleId}-confirm`}>
            {t(confirm.delete ? 'archive.editor.deleteTitle' : 'editor.discardTitle', {
              period: t(period === 'WEEK' ? 'archive.editor.week' : 'archive.editor.month'),
            })}
          </DialogTitle>
          <DialogContent>
            <p>{t(confirm.delete ? 'archive.editor.deleteBody' : 'editor.discardBody')}</p>
          </DialogContent>
          <DialogActions>
            <button autoFocus onClick={cancelConfirm}>
              {t(confirm.delete ? 'common.cancel' : 'editor.keepWriting')}
            </button>
            <button
              onClick={() => {
                if (!lock.current) {
                  const action = confirm.run;
                  setConfirm(null);
                  action();
                }
              }}
            >
              {t(confirm.delete ? 'common.delete' : 'editor.discard')}
            </button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
